import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useTenantStore } from "@/stores/useTenantStore";
import { technicalAuditService } from "@/services/technicalAuditService";
import { useToast } from "@/hooks/useToast";
import { CrawlProgress } from "./CrawlProgress";
import type { CrawlConfig } from "@/types/technicalAudit";

interface StartAuditModalProps {
	onClose: () => void;
	onStarted: (runId: string) => void;
}

export function StartAuditModal({ onClose, onStarted }: StartAuditModalProps) {
	const { currentTenant } = useTenantStore();
	const { showError } = useToast();

	const [config, setConfig] = useState<CrawlConfig>({
		website: currentTenant || "",
		tenant_id: currentTenant || "",
		include_pages: true,
		include_posts: true,
		include_products: false,
		custom_post_types: [],
		include_patterns: [],
		exclude_patterns: ["/tag/*", "/author/*", "/wp-*"],
		date_filter: null,
		days_recent: 0,
		date_from: null,
		date_to: null,
		check_title_meta: true,
		check_h1: true,
		check_alt_text: true,
		check_redirects: true,
		check_broken_links: true,
		check_canonicals: true,
		check_duplicate_titles: true,
		check_duplicate_meta: true,
		check_thin_content: true,
		check_indexability: true,
		check_slow_response: true,
		check_near_duplicate: true,
		check_orphan_page: true,
		check_schema_error: true,
		check_mixed_content: true,
		check_redirect_loop: true,
		check_title_too_short: true,
		check_serp_title_truncated: true,
		check_serp_meta_truncated: true,
		check_multiple_meta: true,
		check_duplicate_h1: true,
		check_low_readability: true,
		check_low_text_ratio: true,
		check_stale_content: true,
		check_broken_external: true,
		check_deep_page: true,
		check_large_image: true,
		check_non_nextgen_image: true,
		check_large_page: true,
		check_unsafe_crossorigin: true,
		check_nofollow_internal: true,
		check_low_internal_links: true,
	});

	const [crawlId, setCrawlId] = useState<string | null>(null);
	const [isStarting, setIsStarting] = useState(false);
	// Set when the backend reused a recent run instead of crawling again, so the
	// user can open it or explicitly force a fresh crawl (no silent duplicates).
	const [dedupedRun, setDedupedRun] = useState<{ runId: string; startedAt?: string } | null>(null);

	const handleStart = async (force = false) => {
		setIsStarting(true);
		setDedupedRun(null);
		try {
			const response = await technicalAuditService.startCrawl({ ...config, force });
			if (response.deduped) {
				setDedupedRun({ runId: response.run_id, startedAt: response.started_at });
				setIsStarting(false);
				return;
			}
			setCrawlId(response.crawl_id);
		} catch (error) {
			showError("Failed to start crawl");
			console.error("Error starting crawl:", error);
			setIsStarting(false);
		}
	};

	// A recent audit already exists — let the user open it or force a fresh crawl.
	if (dedupedRun) {
		const ago = dedupedRun.startedAt
			? Math.max(1, Math.round((Date.now() - new Date(dedupedRun.startedAt).getTime()) / 60000))
			: null;
		return (
			<Dialog open onOpenChange={onClose}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>You already have a recent audit</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">
						This site was audited{ago ? ` about ${ago} minute${ago === 1 ? "" : "s"} ago` : " recently"}.
						Open that audit, or run a fresh crawl anyway.
					</p>
					<div className="mt-4 flex justify-end gap-2">
						<button
							type="button"
							onClick={() => { onStarted(dedupedRun.runId); onClose(); }}
							className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
						>
							Open recent audit
						</button>
						<button
							type="button"
							onClick={() => handleStart(true)}
							className="rounded-md bg-coral px-3 py-1.5 text-sm font-medium text-white hover:bg-coral-hover"
						>
							Run a fresh crawl
						</button>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	// If crawl started, show progress
	if (crawlId) {
		return (
			<Dialog open onOpenChange={onClose}>
				<DialogContent className="max-w-2xl">
					<CrawlProgress
						crawlId={crawlId}
						onComplete={(runId) => {
							onStarted(runId);
							onClose();
						}}
						onCancel={onClose}
					/>
				</DialogContent>
			</Dialog>
		);
	}

	// Show config form
	return (
		<Dialog open onOpenChange={onClose}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Start New Audit</DialogTitle>
				</DialogHeader>

				<div className="space-y-6">
					{/* Website URL */}
					<div>
						<Label>Website URL</Label>
						<Input value={config.website} disabled className="mt-2" />
					</div>

					{/* Content Types */}
					<div>
						<Label>Content Types</Label>
						<div className="flex gap-6 mt-3">
							<div className="flex items-center space-x-2">
								<Checkbox
									id="include-pages"
									checked={config.include_pages}
									onCheckedChange={(checked) =>
										setConfig({ ...config, include_pages: checked as boolean })
									}
								/>
								<label
									htmlFor="include-pages"
									className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
								>
									Pages
								</label>
							</div>
							<div className="flex items-center space-x-2">
								<Checkbox
									id="include-posts"
									checked={config.include_posts}
									onCheckedChange={(checked) =>
										setConfig({ ...config, include_posts: checked as boolean })
									}
								/>
								<label
									htmlFor="include-posts"
									className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
								>
									Posts
								</label>
							</div>
							<div className="flex items-center space-x-2">
								<Checkbox
									id="include-products"
									checked={config.include_products}
									onCheckedChange={(checked) =>
										setConfig({
											...config,
											include_products: checked as boolean,
										})
									}
								/>
								<label
									htmlFor="include-products"
									className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
								>
									Products
								</label>
							</div>
						</div>
					</div>

					{/* Issue Types */}
					<div>
						<Label>Issue Types to Check</Label>
						<div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-3 max-h-[400px] overflow-y-auto pr-2">
							{/* Content Quality */}
							<div className="col-span-2">
								<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Content Quality</p>
							</div>
							{([
								["check_title_meta", "Titles & Meta Descriptions"],
								["check_h1", "H1 Headings"],
								["check_alt_text", "Image Alt Text"],
								["check_thin_content", "Thin Content"],
								["check_near_duplicate", "Near-Duplicate Content"],
								["check_low_readability", "Low Readability"],
								["check_low_text_ratio", "Low Text-to-HTML Ratio"],
								["check_stale_content", "Stale Content"],
							] as const).map(([key, label]) => (
								<div key={key} className="flex items-center space-x-2">
									<Checkbox
										id={key}
										checked={config[key]}
										onCheckedChange={(checked) =>
											setConfig({ ...config, [key]: checked as boolean })
										}
									/>
									<label htmlFor={key} className="text-sm font-medium leading-none">{label}</label>
								</div>
							))}

							{/* Title & Meta */}
							<div className="col-span-2 mt-2">
								<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Title & Meta</p>
							</div>
							{([
								["check_title_too_short", "Title Too Short"],
								["check_serp_title_truncated", "SERP Title Truncated"],
								["check_serp_meta_truncated", "SERP Description Truncated"],
								["check_multiple_meta", "Multiple Meta Descriptions"],
								["check_duplicate_titles", "Duplicate SEO Title Tags"],
								["check_duplicate_meta", "Duplicate Meta Descriptions"],
								["check_duplicate_h1", "Duplicate H1"],
							] as const).map(([key, label]) => (
								<div key={key} className="flex items-center space-x-2">
									<Checkbox
										id={key}
										checked={config[key]}
										onCheckedChange={(checked) =>
											setConfig({ ...config, [key]: checked as boolean })
										}
									/>
									<label htmlFor={key} className="text-sm font-medium leading-none">{label}</label>
								</div>
							))}

							{/* Links */}
							<div className="col-span-2 mt-2">
								<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Links</p>
							</div>
							{([
								["check_broken_links", "Broken Internal Links"],
								["check_broken_external", "Broken External Links"],
								["check_redirects", "Redirect Chains"],
								["check_canonicals", "Canonical Tags"],
								["check_orphan_page", "Orphan Pages"],
								["check_deep_page", "Deep Pages"],
								["check_unsafe_crossorigin", "Unsafe Cross-Origin Links"],
								["check_nofollow_internal", "Nofollow Internal Links"],
								["check_low_internal_links", "Low Internal Links"],
							] as const).map(([key, label]) => (
								<div key={key} className="flex items-center space-x-2">
									<Checkbox
										id={key}
										checked={config[key]}
										onCheckedChange={(checked) =>
											setConfig({ ...config, [key]: checked as boolean })
										}
									/>
									<label htmlFor={key} className="text-sm font-medium leading-none">{label}</label>
								</div>
							))}

							{/* Images */}
							<div className="col-span-2 mt-2">
								<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Images</p>
							</div>
							{([
								["check_large_image", "Oversized Images"],
								["check_non_nextgen_image", "Non-Next-Gen Images"],
							] as const).map(([key, label]) => (
								<div key={key} className="flex items-center space-x-2">
									<Checkbox
										id={key}
										checked={config[key]}
										onCheckedChange={(checked) =>
											setConfig({ ...config, [key]: checked as boolean })
										}
									/>
									<label htmlFor={key} className="text-sm font-medium leading-none">{label}</label>
								</div>
							))}

							{/* Schema & Performance */}
							<div className="col-span-2 mt-2">
								<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schema & Performance</p>
							</div>
							{([
								["check_schema_error", "Schema Validation Errors"],
								["check_indexability", "Indexability Issues"],
								["check_slow_response", "Slow Response Times"],
								["check_large_page", "Oversized Pages"],
							] as const).map(([key, label]) => (
								<div key={key} className="flex items-center space-x-2">
									<Checkbox
										id={key}
										checked={config[key]}
										onCheckedChange={(checked) =>
											setConfig({ ...config, [key]: checked as boolean })
										}
									/>
									<label htmlFor={key} className="text-sm font-medium leading-none">{label}</label>
								</div>
							))}

							{/* Security */}
							<div className="col-span-2 mt-2">
								<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Security</p>
							</div>
							{([
								["check_mixed_content", "Mixed Content"],
								["check_redirect_loop", "Redirect Loops"],
							] as const).map(([key, label]) => (
								<div key={key} className="flex items-center space-x-2">
									<Checkbox
										id={key}
										checked={config[key]}
										onCheckedChange={(checked) =>
											setConfig({ ...config, [key]: checked as boolean })
										}
									/>
									<label htmlFor={key} className="text-sm font-medium leading-none">{label}</label>
								</div>
							))}
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={() => handleStart(false)} disabled={isStarting}>
						{isStarting ? "Starting..." : "Start Audit"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
